/**
 * @file details.layout.tsx
 * @path apps/website/src/components/layouts/details.layout.tsx
 * @description A basic shell layout for details pages, providing global UI and theme settings.
 */

import { cn } from "@sirbastians/utils";
import { useLocation, ScrollRestoration } from "react-router-dom";
import { ReactNode, useEffect } from "react";
import { Navigation } from "./navigation.component";
import { Footer } from "./footer.component";
import { FloatingActions } from "./floatingActions.component";

interface DetailsLayoutProps {
  children: ReactNode;
  bookingLink?: string; // Passed to FloatingActions
  className?: string;
}

export const DetailsLayout = ({
  children,
  bookingLink,
  className,
}: DetailsLayoutProps) => {
  const { pathname } = useLocation();

  // Ensure every navigation starts at the top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [pathname]);

  return (
    <>
      <ScrollRestoration />

      {/* Navigation forced to Light Mode for visibility on details pages */}
      <Navigation lightMode />

      <main
        className={cn(
          "min-h-screen bg-background text-foreground pt-32 pb-16 lg:pt-40 lg:pb-24 px-6 lg:px-14",
          className,
        )}
      >
        {/* Pages will now define their own grids, containers, and hero sections */}
        {children}
      </main>

      <Footer />

      {/* Floating actions with optional booking button */}
      <FloatingActions bookingLink={bookingLink} />
    </>
  );
};
